/* eslint-disable */
// ZPL -> editable components (reverse of labelZpl.js generateZpl).
// Best-effort importer: handles the subset of ZPL this tool emits plus common
// hand-written labels (text, ^FB blocks, ^GB lines/boxes, ^BQ QR, ^BC barcode,
// ^GFA image, ^CF/^A fonts, placeholders). It reverses the generator's encoding:
//  - bold  -> generator fake-bolds by printing the SAME text 3x (origin, +1x, +1y).
//             We collapse those overlapping duplicates back into one isBold field.
//  - highlight -> generator draws a solid ^GB box + ^FR reverse text. We drop the
//             solid box and keep the text with isHighlight (white-on-black).
//  - table -> generator emits an outer ^GB + a grid of cell ^GB boxes + ^FB cell
//             text. We detect that grid and rebuild a single `table` component.
//  - image -> ^GFA carries a 1-bit bitmap; we decode the hex with a canvas ctx
//             (putImageData -> toDataURL) so the picture actually renders.
// Still lossy: printer-config commands (~SD, ^MN ...) and REM comments are ignored;
// compressed ^GFA (RLE) bitmaps are not decoded (this tool emits plain hex).

import qrCodeImg from '../../Images/LabelGenerator/qrCode.jpg';
import parameters from './parameters';
import barCodeImg from '../../Images/LabelGenerator/barCode.jpg';
import labelGeneratorConfig from '../../Components/LabelGenerator/LabelGenerator.config';

// Dropdown ke known placeholder fields (sab label types ka union) ka normalized lookup.
// `{ gross weight }` jaise spaces/case wale ZPL placeholder ko canonical value
// ("Gross Weight") pe map karne ke liye — taaki import hone par text-field ka selected
// field exactly dropdown list se match kare.
const PLACEHOLDER_CANON = (() => {
	const map = new Map();
	const groups = labelGeneratorConfig?.possiblePlaceholderArray || {};
	Object.values(groups).forEach((list) => {
		(list || []).forEach((label) => {
			const key = String(label).trim().toLowerCase();
			if (key && !map.has(key)) map.set(key, label);
		});
	});
	return map;
})();

// braces ke andar ka text -> canonical dropdown value (match na ho to bas trimmed text).
const canonPlaceholder = (inner) => {
	const trimmed = String(inner || '').trim();
	return PLACEHOLDER_CANON.get(trimmed.toLowerCase()) || trimmed;
};

const DPMM = 8;                 // assume 203dpi (8 dots/mm); matches generator default
const DOT_TO_PX = 3 / 8;        // MM_TO_PX(3) / DPMM(8)
const d2p = (d) => Math.round((Number(d) || 0) * DOT_TO_PX);
const dotsToMm = (d) => Math.round((Number(d) || 0) / DPMM);

// Canvas se text width naapo (browser ke same Arial metrics). Isse import pe box ki height
// us text ke barabar banti hai jo editor (RichTextBox) actually wrap karega — na text clip
// hota hai, na box zaroorat se zyada tall. Headless (node) me fallback approx ratio.
const measureCtx = (() => {
	if (typeof document === 'undefined') return null;
	try { return document.createElement('canvas').getContext('2d'); } catch (e) { return null; }
})();
const measureTextW = (text, fontPx) => {
	if (!text) return 0;
	if (!measureCtx) return String(text).length * fontPx * 0.55;
	measureCtx.font = `${fontPx}px Arial, Helvetica, sans-serif`;
	return measureCtx.measureText(String(text)).width;
};
// ek (hard-break) line boxWidth me kitni visual lines me wrap hogi — greedy word-wrap,
// jaise browser karta hai.
const wrappedLineCount = (line, fontPx, boxW) => {
	if (!boxW || boxW <= 0) return 1;
	const parts = String(line).split(/(\s+)/); // spaces preserve
	let cur = '';
	let count = 1;
	for (const p of parts) {
		if (p === '') continue;
		const test = cur + p;
		if (cur.trim() !== '' && measureTextW(test, fontPx) > boxW) {
			count += 1;
			cur = /^\s+$/.test(p) ? '' : p;
		} else {
			cur = test;
		}
	}
	return Math.max(1, count);
};

const orientToRotate = (o) => {
	switch (String(o || 'N').toUpperCase()) {
		case 'R': return 90;
		case 'I': return 180;
		case 'B': return 270;
		default: return 0;
	}
};

const nums = (params) => String(params || '')
	.split(',')
	.map((s) => parseInt(String(s).trim(), 10));

// Tokenize a ZPL string into ordered { code, params } commands.
// ^FD / ^GF capture everything up to the next ^FS (data may contain commas etc).
const tokenize = (zpl) => {
	const s = String(zpl || '');
	const n = s.length;
	const out = [];
	let i = 0;
	while (i < n) {
		const ch = s[i];
		if (ch !== '^' && ch !== '~') { i++; continue; }
		i++; // skip caret/tilde
		if (i >= n) break;
		let code;
		const c1 = (s[i] || '').toUpperCase();
		if (c1 === 'A' && s[i + 1] && /[0-9A-Za-z]/.test(s[i + 1]) && s[i + 1] !== ',') {
			// ^A font command: 'A' + font designator (single char)
			code = 'A';
			i += 1; // params begin at the font designator
		} else {
			code = (s[i] + (s[i + 1] || '')).toUpperCase();
			i += 2;
		}
		if (code === 'FD' || code === 'GF') {
			// read raw until ^FS (case-insensitive)
			const rest = s.slice(i);
			const m = rest.search(/\^FS/i);
			const end = m === -1 ? rest.length : m;
			out.push({ code, params: rest.slice(0, end) });
			i += end;
		} else {
			// params: up to next caret/tilde
			let j = i;
			while (j < n && s[j] !== '^' && s[j] !== '~') j++;
			out.push({ code, params: s.slice(i, j) });
			i = j;
		}
	}
	return out;
};

// strip ZPL escapes from field data into plain text
const decodeData = (raw) => String(raw || '')
	.replace(/\\&/g, '\n')   // ^FB hard line break
	.replace(/\\(.)/g, '$1') // other escapes -> literal
	.trim();

const isPurePlaceholder = (txt) => /^\{[^{}]+\}$/.test(String(txt || '').trim());

let idSeed = 1;
const nextId = () => `imp_${(idSeed++)}_${Math.floor(idSeed * 131 % 100000)}`;

const baseBox = (extra) => ({
	rotate: 0,
	borderRadius: 0,
	borderStyle: 'none',
	borderColor: '#000000',
	thickness: 1,
	selected: false,
	...extra,
});

const DEFAULT_CELL = {
	content: '',
	fontSize: 12,
	fontFamily: '0',
	color: '#000000',
	textAlign: 'left',
	isBold: false,
	isItalic: false,
	isUnderline: false,
	isHighlight: false,
};

// ^FB justification code (L/C/R/J) -> CSS text-align.
const fbAlignToCss = (a) => ({ L: 'left', C: 'center', R: 'right', J: 'justify' }[a] || 'left');

// ---- field -> component builders -------------------------------------------

const makeText = (f) => {
	const fontDots = f.font?.h || f.defFont?.h || 15;
	const fontSize = Math.max(2, d2p(fontDots));
	const data = decodeData(f.data);
	const lines = data.split('\n');
	const longestLine = lines.reduce((a, l) => (l.length > a.length ? l : a), '');

	const padX_dots = Math.round(fontDots * 0.22);
	const padY_dots = Math.round(fontDots * 0.35);

	const width = f.fb?.width
		? d2p(f.fb.width + padX_dots * 2)
		: Math.max(20, Math.ceil(measureTextW(longestLine, fontSize)) + 6);
	// lineHeight: ^FB line-spacing ho to use lo, warna readable default (1.2). Isi pe box ki
	// height bhi banti hai taaki text clip na ho.
	const lineSpaceDots = f.fb?.lineSpace || 0;
	const lineHeight = fontDots > 0 ? Math.max(1.2, (fontDots + lineSpaceDots) / fontDots) : 1.2;
	// Line count ACTUAL canvas measurement se nikaalo (browser jaisa wrap) — taaki box exactly
	// utna tall ho jitna text, na zyada (overlap) na kam (clip). maxLines sirf UPPER cap.
	const wrapW = width > 0 ? width - d2p(padX_dots) * 2 : 0;
	let lineCount = 0;
	for (const ln of lines) lineCount += wrappedLineCount(ln, fontSize, wrapW);
	lineCount = Math.max(1, lineCount);
	if (f.fb?.maxLines) lineCount = Math.min(lineCount, f.fb.maxLines);
	const height = Math.round(fontSize * lineHeight * lineCount) + 4;
	// Classification rule: a pure `{...}` placeholder (sirf ek field, aur kuch nahi) ek
	// bound data field hai -> `text` (dropdown) component. Inner text ko dropdown list se
	// canonical value pe normalize karte hain (`{ gross weight }` -> "Gross Weight").
	// Baaki sab (plain/typed content) free `staticText` (rich text box) rehta hai, chahe
	// single line ho. Generator dono ke liye ^FB emit karta hai, isliye yahan koi aur
	// signal nahi hota.
	const placeholder = isPurePlaceholder(data)
		? canonPlaceholder(data.trim().slice(1, -1))
		: '';
	
	const rawX = f.rawX ?? 0;
	const rawY = f.rawY ?? 0;

	return baseBox({
		id: nextId(),
		name: placeholder ? 'text' : 'staticText',
		content: placeholder || data,
		placeholder,
		fontSize,
		lineHeight,
		fontFamily: '0',
		textAlign: fbAlignToCss(f.fb?.align),
		position: { x: Math.max(0, d2p(rawX - padX_dots)), y: Math.max(0, d2p(rawY - padY_dots)) },
		size: { width, height },
		color: '#000000',
		isBold: false,
		isItalic: false,
		isUnderline: false,
		isHighlight: !!f.reverse,
		rotate: orientToRotate(f.font?.o),
	});
};

// solid black rectangle? generator highlight box = ^GB w,h,h,B (thickness == height).
// A line is ^GB w,t,t (height == thickness) which ALSO satisfies "thickness spans the
// shorter side" — so we additionally require the shorter side to be bigger than a rule
// (lines are a few dots; a highlight fill is at least a font height ~20 dots). Without
// this, every line/rule got turned into a black filled box.
const SOLID_MIN_DOTS = 14;
const isSolidGb = (w, h, t) => {
	const short = Math.min(w || 0, h || 0);
	return Math.max(1, t || 1) >= short && short >= SOLID_MIN_DOTS && (w || 0) > 1 && (h || 0) > 1;
};

const gbToComponent = (f) => {
	const [w = 0, h = 0, t = 1] = nums(f.gb);
	const tt = Math.max(1, t);
	// ^GBw,h,t,color,rounding — the 5th param is the corner rounding (generator emits
	// it for borderRadius). nums() can't read it (B color -> NaN), so pull it raw.
	// rounding is ZPL's 0-8 scale (8 = max), NOT pixels: actual radius ≈
	// (rounding/8) * (min(w,h)/2) in dots. Decode it back to a real px radius so the
	// editor shows the same rounded corners the printer draws (a literal "2px" on a big
	// box was invisible). Inverse of roundingFor() in labelZpl.js.
	const rounding = parseInt(String(f.gb || '').split(',')[4], 10) || 0;
	const radius = rounding > 0
		? d2p((Math.min(8, rounding) / 8) * (Math.min(w, h) / 2))
		: 0;
	// solid filled box -> highlight backdrop; merged into overlapping text later
	if (isSolidGb(w, h, t)) {
		return baseBox({
			id: nextId(), name: '_solidBox',
			position: { x: f.x, y: f.y },
			size: { width: Math.max(2, d2p(w)), height: Math.max(2, d2p(h)) },
			color: '#000000',
		});
	}
	// thin in one dimension -> line; otherwise a rectangle/box outline
	if (h <= tt && w > tt) {
		return baseBox({
			id: nextId(), name: 'line',
			position: { x: f.x, y: f.y },
			size: { width: Math.max(1, d2p(w)), height: Math.max(1, d2p(tt)) },
			color: '#000000', thickness: Math.max(1, d2p(tt)),
		});
	}
	if (w <= tt && h > tt) {
		// Vertical line: editor isko ek horizontal box (width=length) + rotate 90 se draw
		// karta hai jo CENTER ke around rotate hota hai. Agar position ZPL ke top-left pe hi
		// rakhein to center-rotation ke baad line apni jagah se hat ke label se bahar (upar)
		// nikal jaati hai. Position ko compensate karo taaki rotate ke baad vertical line
		// (f.x, f.y) se neeche L tak baithe — ye generator ke line-encoding ka exact inverse:
		//   center=(px+L/2, py+H/2) -> rotate90 -> vertical line @ x=px+L/2, y=center-L/2..+L/2
		const L = Math.max(1, d2p(h));   // line length (px)
		const H = Math.max(1, d2p(tt));  // line thickness (px)
		return baseBox({
			id: nextId(), name: 'line', rotate: 90,
			position: { x: f.x - Math.round(L / 2), y: f.y + Math.round(L / 2) - Math.round(H / 2) },
			size: { width: L, height: H },
			color: '#000000', thickness: H,
		});
	}
	// rectangle/box outline. Stays a `_box` marker: mergeBorders either folds it into
	// an element it wraps (as that element's border) or turns it into a real `rectangle`.
	return baseBox({
		id: nextId(), name: '_box',
		position: { x: f.x, y: f.y },
		size: { width: Math.max(2, d2p(w)), height: Math.max(2, d2p(h)) },
		color: '#000000', fill: false,
		borderStyle: 'solid', borderColor: '#000000', thickness: Math.max(1, d2p(tt)),
		borderRadius: radius,
	});
};

// ^GCd,t,c -> circle (diameter d, border thickness t). Filled when t spans the radius.
const makeCircle = (f) => {
	const [d = 0, t = 1] = nums(f.gc);
	const side = Math.max(2, d2p(d));
	return baseBox({
		id: nextId(), name: 'circle',
		position: { x: f.x, y: f.y },
		size: { width: side, height: side },
		color: '#000000', fill: (t * 2) >= d && d > 0,
		borderStyle: 'solid', borderColor: '#000000', thickness: Math.max(1, d2p(t)),
	});
};

const makeQr = (f) => {
	// ^BQ<orient>,<model>,<mag>
	const parts = String(f.bq || '').split(',');
	const mag = parseInt(parts[2] ?? parts[1] ?? '3', 10) || 3;
	// Generator ka EXACT inverse: labelZpl.js me mag = floor(boxDots / (modules + 8)) hai —
	// quiet zone (4 module har taraf) user ke box ke ANDAR reserve hoti hai. Isliye wapas
	// (modules + 8) * mag karo, warna QR har import pe modules/(modules+8) (~72% v1 pe)
	// shrink ho jaata tha. (Visible QR Labelary me modules*mag hi dikhega — box thoda bada
	// hona hi original design tha.)
	// ^BQ data "<EC><mode>,<data>" hota hai (e.g. "LA,{qrCode}") to pehle EC/mode prefix
	// hatao. Tool-generated ZPL me data hamesha "{qrCode}" placeholder hota hai aur generator
	// ne modules SAMPLE value (parameters.qrCode) ki length se nikale the — wahi lo taaki
	// inverse exact rahe. Literal data (hand-written ZPL) me data ki apni length lo.
	const raw = String(f.data || '').replace(/^[A-Za-z]{1,2},/, '');
	const len = isPurePlaceholder(raw) ? String(parameters?.qrCode ?? '').length : raw.length;
	const modules = len <= 17 ? 21 : len <= 32 ? 25 : len <= 53 ? 29 : len <= 78 ? 33 : 37;
	// ceil (round nahi): pxToDots round karta hai, to round-down hone par floor() me mag ek
	// step gir jaata (6 -> 5). ceil se re-export hamesha wahi mag wapas deta hai.
	const side = Math.max(40, Math.ceil((modules + 8) * mag * DOT_TO_PX));
	// placeholder QR image do (empty src -> canvas pe kuch render nahi hota)
	return baseBox({
		id: nextId(), name: 'qrCode', src: qrCodeImg,
		rotate: orientToRotate(parts[0]),
		position: { x: f.x, y: f.y },
		size: { width: side, height: side },
	});
};

const makeBarcode = (f) => {
	// ^BC<orient>,<height>,...
	const parts = String(f.bc || '').split(',');
	const height = parseInt(parts[1], 10) || 50;
	const data = decodeData(f.data) || '';
	const moduleW = f.by?.moduleW || 1;
	const wDots = (data.length * 11 + 35) * moduleW;
	// placeholder barcode image do (empty src -> canvas pe kuch render nahi hota)
	return baseBox({
		id: nextId(), name: 'barcode', src: barCodeImg,
		rotate: orientToRotate(parts[0]),
		position: { x: f.x, y: f.y },
		size: { width: Math.max(40, d2p(wDots)), height: Math.max(20, d2p(height)) },
	});
};

// ^GFA data ko full hex me decode karo. Plain hex ke alawa ZPL ka ACS (RLE) compression
// bhi handle karta hai jo printers/other tools emit karte hain:
//   G-Y  -> repeat count 1..19 for the next hex nibble (G=1 .. Y=19)
//   g-z  -> repeat count 20,40,..400 (g=20 .. z=400); counts add up
//   ,    -> current row ka baaki hissa 0x00 se bhar ke row end
//   !    -> baaki hissa 0xFF se bhar ke row end
//   :    -> previous row repeat (same as line above)
// Bina iske compressed images (jaise fssai logo) blurry/garbage aati thi kyunki control
// chars strip ho ke nibble alignment toot jaata tha.
const decodeGfaHex = (raw, bytesPerRow) => {
	const rowLen = Math.max(2, (bytesPerRow || 0) * 2);
	const s = String(raw || '');
	// koi ACS control char nahi -> plain hex, seedha clean karke do
	if (!/[ ,:!G-Zg-z\t\r\n]/.test(s)) return s.replace(/[^0-9A-Fa-f]/g, '');
	const rows = [];
	let cur = '';
	let count = 0;
	let emittedFull = false;
	const pad0 = (r) => r.slice(0, rowLen).padEnd(rowLen, '0');
	for (let i = 0; i < s.length; i++) {
		const ch = s[i];
		if (ch === '\n' || ch === '\r' || ch === ' ' || ch === '\t') continue;
		if (ch === ',') { if (cur.length > 0 || !emittedFull) rows.push(pad0(cur)); cur = ''; count = 0; emittedFull = false; continue; }
		if (ch === '!') { rows.push(cur.padEnd(rowLen, 'F').slice(0, rowLen)); cur = ''; count = 0; emittedFull = false; continue; }
		if (ch === ':') { if (cur.length > 0) rows.push(pad0(cur)); rows.push(rows.length ? rows[rows.length - 1] : '0'.repeat(rowLen)); cur = ''; count = 0; emittedFull = false; continue; }
		if (ch >= 'G' && ch <= 'Y') { count += ch.charCodeAt(0) - 70; continue; }       // G(71)=1 .. Y(89)=19
		if (ch >= 'g' && ch <= 'z') { count += (ch.charCodeAt(0) - 102) * 20; continue; } // g(103)=20 .. z(122)=400
		if (/[0-9A-Fa-f]/.test(ch)) {
			cur += ch.toUpperCase().repeat(count || 1);
			count = 0;
			while (cur.length >= rowLen) { rows.push(cur.slice(0, rowLen)); cur = cur.slice(rowLen); emittedFull = (cur.length === 0); }
			continue;
		}
	}
	if (cur.length) rows.push(pad0(cur));
	return rows.join('');
};

// ^GFA bitmap ko canvas ctx se decode kar ke PNG data-url banao.
// gf = "A,<binaryByteCount>,<graphicFieldCount>,<bytesPerRow>,<hexdata>"
const gfaToImage = (gf) => {
	if (typeof document === 'undefined') return null;
	const m = String(gf || '').match(/^\s*([ABC]),\s*(\d+),\s*(\d+),\s*(\d+),([\s\S]*)$/i);
	if (!m) return null;
	const bytesPerRow = parseInt(m[4], 10) || 0;
	const hex = decodeGfaHex(m[5] || '', bytesPerRow); // plain hex + ACS(RLE) dono handle
	if (!bytesPerRow || hex.length < 2) return null;
	const totalBytes = Math.floor(hex.length / 2);
	const rows = Math.floor(totalBytes / bytesPerRow);
	if (rows <= 0) return null;
	const w = bytesPerRow * 8;
	const h = rows;
	try {
		const canvas = document.createElement('canvas');
		canvas.width = w;
		canvas.height = h;
		const ctx = canvas.getContext('2d');
		const imgData = ctx.createImageData(w, h);
		const px = imgData.data;
		for (let row = 0; row < rows; row++) {
			for (let bx = 0; bx < bytesPerRow; bx++) {
				const byte = parseInt(hex.substr((row * bytesPerRow + bx) * 2, 2), 16) || 0;
				for (let bit = 0; bit < 8; bit++) {
					const x = bx * 8 + bit;
					if (x >= w) break;
					const dark = (byte >> (7 - bit)) & 1; // 1 = black (generator convention)
					const idx = (row * w + x) * 4;
					const v = dark ? 0 : 255;
					px[idx] = v; px[idx + 1] = v; px[idx + 2] = v; px[idx + 3] = 255;
				}
			}
		}
		ctx.putImageData(imgData, 0, 0);
		return { src: canvas.toDataURL('image/png'), w, h };
	} catch (e) {
		return null;
	}
};

const makeImage = (f) => {
	const dec = gfaToImage(f.gf);
	if (dec) {
		return baseBox({
			id: nextId(), name: 'image', src: dec.src, file: null,
			position: { x: f.x, y: f.y },
			size: { width: Math.max(20, d2p(dec.w)), height: Math.max(20, d2p(dec.h)) },
		});
	}
	// decode fail -> empty image box (upload dropzone), size from headers
	const parts = String(f.gf || '').replace(/^[ABC],?/i, '').split(',');
	const bytesPerRow = parseInt(parts[2], 10) || 1;
	const fieldBytes = parseInt(parts[1], 10) || 0;
	const rows = bytesPerRow ? Math.round(fieldBytes / bytesPerRow) : 0;
	return baseBox({
		id: nextId(), name: 'image', src: '', file: null,
		position: { x: f.x, y: f.y },
		size: { width: Math.max(20, d2p(bytesPerRow * 8)), height: Math.max(20, d2p(rows) || 40) },
	});
};

// classify a finished field (opened by ^FO/^FT, closed by ^FS) into a known type
const classify = (f) => {
	if (!f) return null;
	if (f.gf != null) { f.type = 'image'; return f; }
	if (f.bq != null) { f.type = 'qr'; return f; }
	if (f.bc != null) { f.type = 'barcode'; return f; }
	if (f.gb != null) { f.type = 'gb'; return f; }
	if (f.gc != null) { f.type = 'circle'; return f; }
	if (f.data != null && decodeData(f.data) !== '') { f.type = 'text'; return f; }
	return null;
};

const fieldToComponent = (f) => {
	switch (f.type) {
		case 'image': return makeImage(f);
		case 'qr': return makeQr(f);
		case 'barcode': return makeBarcode(f);
		case 'gb': return gbToComponent(f);
		case 'circle': return makeCircle(f);
		case 'text': return makeText(f);
		default: return null;
	}
};

// ---- table reconstruction ---------------------------------------------------
// Generator emits a table as: outer ^GB box, then a grid of cell ^GB boxes (in
// row-major order, first cell sharing the outer's top-left), each optionally
// followed by a solid highlight box / border / ^FB text. Detect that block.
const clusterPositions = (vals, tol = 3) => {
	const sorted = [...vals].sort((a, b) => a - b);
	const groups = [];
	for (const v of sorted) {
		const last = groups[groups.length - 1];
		if (last != null && Math.abs(last - v) <= tol) continue;
		groups.push(v);
	}
	return groups;
};

const tryParseTable = (fields, start) => {
	const outer = fields[start];
	if (!outer || outer.type !== 'gb') return null;
	const [W = 0, H = 0, T = 1] = nums(outer.gb);
	if (isSolidGb(W, H, T) || W <= 2 || H <= 2) return null;
	const ox = outer.x;
	const oy = outer.y;
	const owPx = d2p(W);
	const ohPx = d2p(H);

	// gather the contiguous run of fields that fall inside the outer box
	const within = (g) => g.x >= ox - 3 && g.y >= oy - 3
		&& g.x <= ox + owPx + 3 && g.y <= oy + ohPx + 3;
	let j = start + 1;
	const block = [];
	while (j < fields.length && within(fields[j])) { block.push(fields[j]); j++; }
	if (!block.length) return null;

	// cell boxes = thin (non-solid) ^GB boxes inside the outer
	const cellBoxes = [];
	for (const g of block) {
		if (g.type !== 'gb') continue;
		const [w = 0, h = 0, t = 1] = nums(g.gb);
		if (!isSolidGb(w, h, t) && w > 2 && h > 2) {
			cellBoxes.push({ x: g.x, y: g.y, w: d2p(w), h: d2p(h) });
		}
	}
	if (cellBoxes.length < 2) return null;

	const colX = clusterPositions(cellBoxes.map((b) => b.x));
	const rowY = clusterPositions(cellBoxes.map((b) => b.y));
	const cols = colX.length;
	const rows = rowY.length;
	if (rows * cols < 2 || cellBoxes.length < rows * cols) return null;
	// first cell must sit at the outer top-left (generator signature)
	if (Math.abs(colX[0] - ox) > 3 || Math.abs(rowY[0] - oy) > 3) return null;

	const colW = colX.map((cx, ci) => {
		const b = cellBoxes.find((bb) => Math.abs(bb.x - cx) <= 3);
		return b ? b.w : Math.round(owPx / cols);
	});
	const rowH = rowY.map((ry) => {
		const b = cellBoxes.find((bb) => Math.abs(bb.y - ry) <= 3);
		return b ? b.h : Math.round(ohPx / rows);
	});

	const cells = Array.from({ length: rows }, () =>
		Array.from({ length: cols }, () => ({ ...DEFAULT_CELL })));
	const findCell = (px, py) => {
		let ci = 0;
		let ri = 0;
		for (let k = cols - 1; k >= 0; k--) { if (px >= colX[k] - 4) { ci = k; break; } }
		for (let k = rows - 1; k >= 0; k--) { if (py >= rowY[k] - 4) { ri = k; break; } }
		return { ri, ci };
	};

	for (const g of block) {
		if (g.type === 'text') {
			const data = decodeData(g.data);
			if (!data) continue;
			const { ri, ci } = findCell(g.x, g.y);
			const cell = cells[ri]?.[ci];
			if (!cell) continue;
			if (cell.content && cell.content === data) {
				cell.isBold = true; // fake-bold duplicate pass
			} else if (!cell.content) {
				cell.content = data;
				cell.fontSize = Math.max(2, d2p(g.font?.h || g.defFont?.h || 12));
				cell.rotate = orientToRotate(g.font?.o);
				cell.textAlign = fbAlignToCss(g.fb?.align);
				if (g.reverse) cell.isHighlight = true;
			}
		} else if (g.type === 'gb') {
			const [w = 0, h = 0, t = 1] = nums(g.gb);
			if (isSolidGb(w, h, t)) {
				const { ri, ci } = findCell(g.x, g.y);
				if (cells[ri]?.[ci]) cells[ri][ci].isHighlight = true;
			}
		}
	}

	const comp = baseBox({
		id: nextId(), name: 'table',
		position: { x: ox, y: oy },
		size: { width: owPx, height: ohPx },
		rows, cols, cells,
		colWidths: colW,
		rowHeights: rowH,
		color: '#000000',
		borderStyle: 'solid',
		borderColor: '#000000',
		thickness: Math.max(1, d2p(T)),
	});
	return { comp, next: j };
};

// ---- post-processing --------------------------------------------------------

const rectOf = (c) => ({
	x: c.position?.x || 0,
	y: c.position?.y || 0,
	w: c.size?.width || 0,
	h: c.size?.height || 0,
});
const overlaps = (a, b) => {
	const r1 = rectOf(a);
	const r2 = rectOf(b);
	return r1.x < r2.x + r2.w + 2 && r1.x + r1.w + 2 > r2.x
		&& r1.y < r2.y + r2.h + 2 && r1.y + r1.h + 2 > r2.y;
};
const near = (a, b, tol = 2) => Math.abs((a || 0) - (b || 0)) <= tol;
const cornerDist = (a, b) => Math.hypot(
	(a.position?.x || 0) - (b.position?.x || 0),
	(a.position?.y || 0) - (b.position?.y || 0),
);

// generator fake-bolds text by printing it 3x at overlapping positions -> collapse
const collapseBold = (comps) => {
	const out = [];
	for (const c of comps) {
		if (c.name === 'text' || c.name === 'staticText') {
			const dup = out.find((o) =>
				o.name === c.name
				&& o.content === c.content
				&& (o.placeholder || '') === (c.placeholder || '')
				&& near(o.position?.x, c.position?.x)
				&& near(o.position?.y, c.position?.y));
			if (dup) { dup.isBold = true; continue; }
		}
		out.push(c);
	}
	return out;
};

// generator highlight = solid ^GB box behind ^FR reverse text. Drop the box and
// keep the text flagged isHighlight; a lone solid box becomes a black filled box.
const mergeHighlights = (comps) => {
	const texts = comps.filter((c) => c.name === 'text' || c.name === 'staticText');
	const drop = new Set();
	for (const s of comps) {
		if (s.name !== '_solidBox') continue;
		// a highlight box shares its text's ^FO origin, but a wide box can also overlap a
		// neighbouring field — so pick the overlapping text nearest the box's origin, not
		// just the first one in document order (which highlighted the wrong field).
		const tgt = texts
			.filter((o) => overlaps(s, o))
			.sort((a, b) => cornerDist(s, a) - cornerDist(s, b))[0];
		if (tgt) {
			tgt.isHighlight = true;
			drop.add(s.id);
		} else {
			// standalone solid box -> a filled (black) rectangle shape
			s.name = 'rectangle';
			s.fill = true;
			s.color = '#000000';
			s.borderStyle = 'none';
			s.borderColor = '#000000';
		}
	}
	return comps.filter((c) => !drop.has(c.id));
};

const norm90 = (r) => (((Math.round((Number(r) || 0) / 90) * 90) % 360) + 360) % 360;
const isRotated90 = (c) => norm90(c.rotate) === 90 || norm90(c.rotate) === 270;
const areaOf = (c) => (c.size?.width || 0) * (c.size?.height || 0);
const isGraphic = (c) => c.name === 'barcode' || c.name === 'qrCode' || c.name === 'image';

// A `_box` (^GB outline) may be an element's border: the generator draws it as a
// separate ^GB sharing the element's ^FO origin (borderZpl). Fold such a wrapper box
// into the element it surrounds (as that element's border + radius) and drop it. We
// match on a shared top-left origin (corner distance) so the full-card border and
// unrelated boxes — far from any element's origin — are never absorbed. Any `_box` that
// isn't a wrapper is a standalone drawn shape -> a real `rectangle` component.
const mergeBorders = (comps) => {
	const targets = comps.filter((c) =>
		((c.name === 'text' || c.name === 'staticText') && String(c.content || '').trim() !== '')
		|| c.name === 'barcode' || c.name === 'qrCode' || c.name === 'image');
	const drop = new Set();
	for (const box of comps) {
		if (box.name !== '_box') continue;
		const tgt = targets
			.filter((t) => !drop.has(t.id) && t.id !== box.id
				&& cornerDist(box, t) <= 18 && overlaps(box, t)
				// for graphics the box defines the true (often bigger) bounds; for text a
				// real border hugs the text — a box much larger than the text is a drawn
				// rectangle/container (e.g. a table outline), not that text's border.
				&& (isGraphic(t) || areaOf(box) <= areaOf(t) * 4 + 1))
			.sort((a, b) => cornerDist(box, a) - cornerDist(box, b))[0];
		if (tgt) {
			tgt.borderStyle = 'solid';
			tgt.borderColor = box.borderColor || '#000000';
			tgt.thickness = box.thickness || 1;
			tgt.borderRadius = box.borderRadius || 0;
			// The border box equals the element's TRUE drawn bounds, so adopt its size
			// (data/text-estimated size is only a guess). For 90/270 the box is the rotated
			// bounding box, so swap to un-rotated dims.
			const bw = box.size?.width || 0;
			const bh = box.size?.height || 0;
			if (isGraphic(tgt)) {
				tgt.size = isRotated90(tgt) ? { width: bh, height: bw } : { width: bw, height: bh };
			} else {
				// text/staticText (rich text box): border box hi rich-text box ki asli w/h hai.
				// Pehle fold ke baad text apne estimate-size pe rehta tha (galat w/h) -> content
				// theek se fit/wrap nahi hota tha. Ab position + size dono box se lo.
				tgt.position = { x: box.position?.x || 0, y: box.position?.y || 0 };
				tgt.size = isRotated90(tgt) ? { width: bh, height: bw } : { width: bw, height: bh };
			}
			drop.add(box.id);
		}
	}
	// any `_box` that wasn't a wrapper is a standalone drawn rectangle
	return comps
		.filter((c) => !drop.has(c.id))
		.map((c) => (c.name === '_box' ? { ...c, name: 'rectangle' } : c));
};

// generator places rotated barcodes/qr at a rotated field origin (^FO shifted by
// (w-h)/2, see fieldOrigin in labelZpl). Reverse that so the component lands back at
// its real top-left, matching where the user placed it.
const unrotatePositions = (comps) => {
	for (const c of comps) {
		if (c.name !== 'barcode' && c.name !== 'qrCode') continue;
		if (!isRotated90(c)) continue;
		const w = c.size?.width || 0;
		const h = c.size?.height || 0;
		c.position = {
			x: (c.position?.x || 0) - Math.round((w - h) / 2),
			y: (c.position?.y || 0) - Math.round((h - w) / 2),
		};
	}
	return comps;
};

// ---- rich text reconstruction ----------------------------------------------
// A staticText with inline bold/highlight is emitted by the generator as MANY per-run
// ^FD fields (richRunsZpl): one fragment per word / format-run, laid out in lines. By
// this point collapseBold/mergeHighlights/mergeBorders have given each fragment its
// isBold / isHighlight / border. Re-group the fragments that flow together (same font
// size, adjacent) and rebuild ONE staticText whose content is HTML (<b> for bold runs,
// a black/white <span> for highlighted runs, a font-size <span> for odd-sized runs).
const escHtml = (s) => String(s || '')
	.replace(/&/g, '&amp;')
	.replace(/</g, '&lt;')
	.replace(/>/g, '&gt;');

const HL_SPAN_OPEN = '<span style="background-color: rgb(0, 0, 0); color: rgb(255, 255, 255);">';

// expand a run's rect by ~a glyph so word/line gaps still count as "touching"
const richExpand = (c) => {
	const r = rectOf(c);
	const fs = Number(c.fontSize) || 14;
	return { x: r.x - fs * 0.8, y: r.y - fs * 0.6, w: r.w + fs * 1.6, h: r.h + fs * 1.2 };
};
const rectsHit = (a, b) => a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;

const mergeRichText = (comps) => {
	const isRun = (c) => (c.name === 'text' || c.name === 'staticText')
		&& String(c.content || '').trim() !== ''
		&& !(c.placeholder && String(c.placeholder).trim() !== '');
	const runs = comps.filter(isRun);
	const used = new Set();
	const groups = [];
	// connected components: same font size + overlapping (expanded) rects
	for (const seed of runs) {
		if (used.has(seed.id)) continue;
		const group = [seed];
		used.add(seed.id);
		const queue = [seed];
		while (queue.length) {
			const cur = queue.shift();
			const ce = richExpand(cur);
			for (const other of runs) {
				if (used.has(other.id)) continue;
				if ((other.fontSize || 0) !== (cur.fontSize || 0)) continue; // exact size only
				if (rectsHit(ce, richExpand(other))) {
					used.add(other.id);
					group.push(other);
					queue.push(other);
				}
			}
		}
		// Only fragment clusters from the generator's rich-text path get merged. That path
		// runs ONLY for inline bold/highlight, so a real cluster always has a bold or
		// highlighted fragment. Plain fields that merely sit near each other (table cells,
		// stacked labels in a hand-written ZPL) must stay separate.
		if (group.length >= 2 && group.some((g) => g.isBold || g.isHighlight)) groups.push(group);
	}
	if (!groups.length) return comps;

	const dropIds = new Set();
	const merged = [];
	for (const group of groups) {
		group.forEach((g) => dropIds.add(g.id));
		const sorted = [...group].sort((a, b) => (a.position?.y || 0) - (b.position?.y || 0)
			|| (a.position?.x || 0) - (b.position?.x || 0));
		const fs = sorted[0].fontSize || 14;
		// cluster fragments into rows by y, then order each row left->right
		const rowTol = fs * 0.6;
		const rows = [];
		for (const r of sorted) {
			const y = r.position?.y || 0;
			let row = rows.find((rw) => Math.abs(rw.y - y) <= rowTol);
			if (!row) { row = { y, items: [] }; rows.push(row); }
			row.items.push(r);
		}
		rows.sort((a, b) => a.y - b.y);
		// highlighted runs never carry the bold triple-print, so judge "all bold" from
		// the non-highlighted runs only (highlighted text inherits the element bold).
		const nonHl = group.filter((g) => !g.isHighlight);
		const allBold = nonHl.length > 0 && nonHl.every((g) => g.isBold);
		const allHl = group.every((g) => g.isHighlight);

		const lineStrs = rows.map((row) => {
			const items = row.items.sort((a, b) => (a.position?.x || 0) - (b.position?.x || 0));
			// runs are word fragments — merge consecutive runs with identical formatting
			// into one segment (text joined by spaces) so a multi-word highlight becomes
			// a single <span>, and always keep a blank space between adjacent runs.
			const segs = [];
			for (const it of items) {
				const key = `${!!it.isBold}|${!!it.isHighlight}|${it.fontSize || fs}`;
				const last = segs[segs.length - 1];
				if (last && last.key === key) {
					last.text += ` ${String(it.content || '')}`;
				} else {
					segs.push({ key, text: String(it.content || ''), bold: !!it.isBold, hl: !!it.isHighlight, size: it.fontSize || fs });
				}
			}
			return segs.map((sg) => {
				let inner = escHtml(sg.text);
				if (sg.size !== fs) inner = `<span style="font-size: ${sg.size}px;">${inner}</span>`;
				if (sg.hl && !allHl) inner = `${HL_SPAN_OPEN}${inner}</span>`;
				if (sg.bold && !allBold) inner = `<b>${inner}</b>`;
				return inner;
			}).join(' ');
		});
		const content = lineStrs.join(' ');

		const xs = group.map((g) => g.position?.x || 0);
		const ys = group.map((g) => g.position?.y || 0);
		const rights = group.map((g) => (g.position?.x || 0) + (g.size?.width || 0));
		const bottoms = group.map((g) => (g.position?.y || 0) + (g.size?.height || 0));
		const minX = Math.min(...xs);
		const minY = Math.min(...ys);
		const bordered = group.find((g) => g.borderStyle === 'solid');

		merged.push(baseBox({
			id: nextId(),
			name: 'staticText',
			content,
			placeholder: '',
			fontSize: fs,
			lineHeight: 1.2,
			fontFamily: group[0].fontFamily || '0',
			position: { x: minX, y: minY },
			size: {
				width: Math.max(...rights) - minX,
				height: Math.max(...bottoms) - minY,
			},
			color: '#000000',
			isBold: allBold,
			isItalic: false,
			isUnderline: false,
			isHighlight: allHl,
			borderStyle: bordered ? 'solid' : 'none',
			borderColor: bordered?.borderColor || '#000000',
			thickness: bordered?.thickness || 1,
			borderRadius: bordered?.borderRadius || 0,
		}));
	}
	return comps.filter((c) => !dropIds.has(c.id)).concat(merged);
};

const buildComponents = (fields) => {
	const comps = [];
	let i = 0;
	while (i < fields.length) {
		const f = fields[i];
		if (f.type === 'gb') {
			const table = tryParseTable(fields, i);
			if (table) { comps.push(table.comp); i = table.next; continue; }
		}
		const c = fieldToComponent(f);
		if (c) comps.push(c);
		i++;
	}
	// mergeRichText BEFORE mergeBorders: the generator emits a multi-line rich-text box as
	// one border ^GB + many per-word ^FD fragments. If borders merge first, the wide box is
	// far bigger than any single word fragment, fails the area gate, and wrongly becomes a
	// standalone `rectangle`. Merging the rich text first rebuilds ONE staticText whose
	// bounds match the box, so mergeBorders folds it in as that text's border (+ radius).
	return unrotatePositions(mergeBorders(mergeRichText(mergeHighlights(collapseBold(comps)))));
};

// ---- main entry -------------------------------------------------------------

// fallbackSize = stored label size (mm) from the saved JSON. Used only when the ZPL
// itself carries no ^PW/^LL (older labels emitted without them) so we don't drop to the
// generic 100x60 default. ZPL dimensions, when present, always win.
export const parseZplToLabel = (zpl, fallbackSize) => {
	const cmds = tokenize(zpl);
	const lh = { x: 0, y: 0 };
	let pw = 0;
	let ll = 0;
	let defFont = { h: 15, w: 15 };
	let by = { moduleW: 2, ratio: 3, height: 10 };
	const fields = [];
	let f = null; // pending field, opened by ^FO/^FT, closed by ^FS

	const pushField = (fld) => {
		const c = classify(fld);
		if (c) fields.push(c);
	};

	const startField = (params) => {
		pushField(f); // safety: close any unterminated field
		const [x = 0, y = 0] = nums(params);
		f = { x: d2p(lh.x + x), y: d2p(lh.y + y), rawX: lh.x + x, rawY: lh.y + y, defFont };
	};

	for (const { code, params } of cmds) {
		switch (code) {
			case 'PW': pw = nums(params)[0] || 0; break;
			case 'LL': ll = nums(params)[0] || 0; break;
			case 'LH': { const [x = 0, y = 0] = nums(params); lh.x = x; lh.y = y; break; }
			case 'CF': {
				const designator = (params || '0')[0];
				const hh = parseInt((params.split(',')[1]) || '', 10);
				const ww = parseInt((params.split(',')[2]) || '', 10);
				defFont = { h: hh || defFont.h, w: ww || hh || defFont.w, designator };
				break;
			}
			case 'BY': {
				const [m = 2, r = 3, h = 10] = nums(params);
				by = { moduleW: m || 2, ratio: r || 3, height: h || 10 };
				break;
			}
			case 'FO': case 'FT': startField(params); break;
			case 'A': {
				if (!f) break;
				// params: <designator><orient>,<h>,<w>. Strip the designator + optional
				// orient letter, then take the numbers that remain. Some generators emit
				// an empty orientation field, e.g. "0,,22.64" (height as the 3rd value) —
				// taking the first NON-empty number as the height handles both forms,
				// where a fixed comma-strip would lose it and shrink the text to min size.
				const orient = (params.match(/^.([NRIB])/i) || [])[1] || 'N';
				const after = params.replace(/^.[NRIB]?/i, '');
				const numbers = after.split(',').map((x) => parseFloat(x)).filter((x) => !Number.isNaN(x));
				const [h, w] = numbers;
				f.font = { h: h || defFont.h, w: w || h || defFont.w, o: orient };
				break;
			}
			case 'FB': { if (f) { const [w, lines, space] = nums(params); const just = String((params.split(',')[3] || 'L')).trim().toUpperCase(); f.fb = { width: w || 0, maxLines: Math.max(1, lines || 1), lineSpace: Math.max(0, space || 0), align: /^[LCRJ]$/.test(just) ? just : 'L' }; } break; }
			case 'FR': if (f) f.reverse = true; break;
			case 'GB': if (f) f.gb = params; break;
			case 'GC': if (f) f.gc = params; break;
			case 'GF': if (f) f.gf = params; break;
			case 'BQ': if (f) f.bq = params; break;
			case 'BC': if (f) { f.bc = params; f.by = by; } break;
			case 'FD': if (f) f.data = params; break;
			case 'FS': pushField(f); f = null; break;
			default: break;
		}
	}
	pushField(f);

	// The label's own border (^FO0,0^GB pw,ll,t) imports as a full-card empty bordered
	// staticText, which would show the "enter text in this" placeholder over the whole
	// card. The editor already draws the card border from the label settings, so drop
	// that redundant box.
	const labelW = d2p(pw);
	const labelH = d2p(ll);
	const isCardBorder = (c) => c.name === 'rectangle'
		&& !c.fill
		&& c.borderStyle === 'solid'
		&& Math.abs(c.position?.x || 0) <= 4 && Math.abs(c.position?.y || 0) <= 4
		&& labelW > 0 && labelH > 0
		&& Math.abs((c.size?.width || 0) - labelW) <= 6
		&& Math.abs((c.size?.height || 0) - labelH) <= 6;

	const fbW = Number(fallbackSize?.width) || 0;
	const fbH = Number(fallbackSize?.height) || 0;
	// sizeFromZpl: did the ZPL actually carry both ^PW and ^LL? If not, the width/height
	// shown is the stored/fallback one — the caller warns the user to verify it.
	const sizeFromZpl = !!(pw && ll);
	return {
		size: {
			width: dotsToMm(pw) || fbW || 100,
			height: dotsToMm(ll) || fbH || 60,
		},
		sizeFromZpl,
		dpmm: DPMM,
		components: buildComponents(fields).filter((c) => !isCardBorder(c)),
	};
};

export default parseZplToLabel;
